import { FC, useEffect, useRef } from 'react';

import prostudioMark from '../assets/home/prostudio-mark.svg?url';
import knowledgeLogo from './design-agents/knowledge-agent.svg?url';

/**
 * The "Preview" demos on Start's two cards (prototype `.path-demo`,
 * `playArt` / `restArt`): each is a small picture of that path — the chat,
 * or the four setup screens — which replays while its card is peeked and
 * rests on its finished frame otherwise. Pictures, not product: nothing in
 * them is interactive, and they are hidden from assistive tech.
 *
 * Driven straight on the DOM, like the prototype, because the animation is
 * a timeline of text being typed and classes flipped; React state per
 * keystroke would re-render the whole card for no gain.
 */

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** A cancellable timeline: every step checks it's still the live run. */
const timeline = () => {
  let alive = true;
  const wait = (ms: number) =>
    new Promise<boolean>((resolve) =>
      window.setTimeout(() => resolve(alive), ms)
    );
  // A person's typing: uneven. The solution's: fast and even.
  const type = async (
    el: HTMLElement,
    text: string,
    [ms, jitter]: [number, number]
  ) => {
    for (let i = 1; i <= text.length; i += 1) {
      if (!alive) return false;
      el.textContent = text.slice(0, i);
      // eslint-disable-next-line no-await-in-loop
      await wait(
        ms + Math.random() * jitter + (text[i - 1] === ' ' ? jitter : 0)
      );
    }
    return alive;
  };
  return {
    wait,
    type,
    alive: () => alive,
    stop: () => {
      alive = false;
    },
  };
};

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="10"
    height="10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);

const TALK_ASK = 'Hey, what should this solution do for your customers?';
const TALK_ANSWER = 'Answer policy questions';
const TALK_REPLY = 'Got it. Where do the answers come from?';

/** Talk it through: types an answer into the box, sends it, thinks, replies. */
export const TalkDemo: FC<{ playing: boolean }> = ({ playing }) => {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const art = root.current;
    if (!art) return undefined;
    const $ = <T extends HTMLElement>(q: string) => art.querySelector(q) as T;
    const line = $('.c-demo-talk__hero .c-demo-talk__line');
    const typed = $('.c-demo-talk__hero .c-demo-talk__typed');
    const ph = $('.c-demo-talk__hero .c-demo-talk__ph');
    const send = $('.c-demo-talk__hero .c-demo-talk__send');
    const msgs = [1, 2, 3].map((i) => $(`.c-demo-talk__msg--${i}`));
    const reply = $('.c-demo-talk__reply');

    // At rest: the finished conversation.
    const rest = () => {
      delete art.dataset.play;
      msgs.forEach((m) => m.classList.remove('is-off', 'is-thinking'));
      reply.textContent = TALK_REPLY;
      line.classList.remove('is-typing');
    };
    if (!playing || reducedMotion()) {
      rest();
      return undefined;
    }

    const t = timeline();
    (async () => {
      while (t.alive()) {
        typed.textContent = '';
        ph.hidden = false;
        send.classList.remove('is-on');
        line.classList.remove('is-typing');
        msgs.forEach((m) => m.classList.add('is-off'));
        msgs[2].classList.remove('is-thinking');
        art.dataset.play = 'hero';
        if (!(await t.wait(700))) break;
        ph.hidden = true;
        line.classList.add('is-typing');
        if (!(await t.type(typed, TALK_ANSWER, [70, 60]))) break;
        send.classList.add('is-on');
        if (!(await t.wait(450))) break;
        line.classList.remove('is-typing');
        art.dataset.play = 'convo';
        await t.wait(200);
        msgs[0].classList.remove('is-off');
        await t.wait(260);
        msgs[1].classList.remove('is-off');
        if (!(await t.wait(420))) break;
        msgs[2].classList.add('is-thinking');
        msgs[2].classList.remove('is-off');
        if (!(await t.wait(1500))) break;
        reply.textContent = '';
        msgs[2].classList.remove('is-thinking');
        if (!(await t.type(reply, TALK_REPLY, [16, 6]))) break;
        await t.wait(2600);
      }
    })();
    return () => {
      t.stop();
      rest();
    };
  }, [playing]);

  return (
    <div className="c-demo c-demo-talk" ref={root} aria-hidden="true">
      <div className="c-demo-talk__screen">
        <div className="c-demo-talk__hero">
          <p className="c-demo-talk__title">{TALK_ASK}</p>
          <div className="c-demo-talk__box">
            <span className="c-demo-talk__line">
              <span className="c-demo-talk__ph">
                Describe it in your own words…
              </span>
              <span className="c-demo-talk__typed" />
              <i className="c-demo-talk__caret" />
            </span>
            <span className="c-demo-talk__send">
              <SendIcon />
            </span>
          </div>
        </div>
        <div className="c-demo-talk__convo">
          <div className="c-demo-talk__thread">
            <div className="c-demo-talk__msg c-demo-talk__msg--ai c-demo-talk__msg--1">
              <span>{TALK_ASK}</span>
            </div>
            <div className="c-demo-talk__msg c-demo-talk__msg--you c-demo-talk__msg--2">
              <span>{TALK_ANSWER}</span>
            </div>
            <div className="c-demo-talk__msg c-demo-talk__msg--ai c-demo-talk__msg--3">
              <span className="c-demo-talk__think">
                <img src={prostudioMark} alt="" width={12} height={12} />
                Thinking…
              </span>
              <span className="c-demo-talk__reply">{TALK_REPLY}</span>
            </div>
          </div>
          <div className="c-demo-talk__box c-demo-talk__box--foot">
            <span className="c-demo-talk__ph">Reply…</span>
            <span className="c-demo-talk__send">
              <SendIcon />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RULES = ['1–100 characters', 'Unique in this project'];

/** The four setup screens, each a function of how far its fill has got. */
const FRAMES: { title: string; ka?: boolean; body: JSX.Element }[] = [
  {
    title: 'Create a new solution',
    body: (
      <>
        <span className="c-demo-steps__label">Solution title*</span>
        <span className="c-demo-steps__field">
          <span className="c-demo-steps__typed" data-full="Bobcat" />
          <i className="c-demo-steps__caret" />
        </span>
        <span className="c-demo-steps__rules">
          {RULES.map((r) => (
            <span key={r} className="c-demo-steps__rule c-demo-steps__reveal">
              ✓ {r}
            </span>
          ))}
        </span>
        <span className="c-demo-steps__pair c-demo-steps__reveal">
          <span className="c-demo-steps__col">
            <span className="c-demo-steps__label">AI provider</span>
            <span className="c-demo-steps__field c-demo-steps__field--sel">
              <span>Gemini</span>
              <b>⌄</b>
            </span>
          </span>
          <span className="c-demo-steps__col">
            <span className="c-demo-steps__label">Model type</span>
            <span className="c-demo-steps__field c-demo-steps__field--sel">
              <span>gemini-2.5-flash</span>
              <b>⌄</b>
            </span>
          </span>
        </span>
      </>
    ),
  },
  {
    title: 'Solution name',
    body: (
      <>
        <span className="c-demo-steps__note">
          How the solution will refer to itself, e.g. Hi, I’m Emily your digital
          assistant.
        </span>
        <span className="c-demo-steps__label">Solution name*</span>
        <span className="c-demo-steps__field">
          <span
            className="c-demo-steps__typed"
            data-full="Bobby, your insurance assistant"
          />
          <i className="c-demo-steps__caret" />
        </span>
      </>
    ),
  },
  {
    title: 'Create a Knowledge Agent',
    ka: true,
    body: (
      <>
        <span className="c-demo-steps__label">Brand name*</span>
        <span className="c-demo-steps__field">
          <span className="c-demo-steps__typed" data-full="Bobcat Insurance" />
          <i className="c-demo-steps__caret" />
        </span>
        <span className="c-demo-steps__label">Tone</span>
        <span className="c-demo-steps__field">
          <span
            className="c-demo-steps__typed"
            data-full="Friendly and plain-spoken, no jargon"
          />
          <i className="c-demo-steps__caret" />
        </span>
        <span className="c-demo-steps__label">Documents</span>
        <span className="c-demo-steps__drop">
          <span className="c-demo-steps__drop-hint">↑</span>
          {['Policy handbook.pdf', 'Claims FAQ.docx'].map((f) => (
            <span key={f} className="c-demo-steps__file c-demo-steps__reveal">
              {f}
            </span>
          ))}
        </span>
      </>
    ),
  },
  {
    title: 'Define tasks',
    body: (
      <span className="c-demo-steps__tasks">
        {['Policy questions', 'Claim status', 'Agent escalation'].map((t) => (
          <span key={t} className="c-demo-steps__task c-demo-steps__reveal">
            <b>◎</b>
            <span className="c-demo-steps__task-name">{t}</span>
            <i>⌄</i>
          </span>
        ))}
        <span className="c-demo-steps__addtask c-demo-steps__reveal">
          <b>+</b>Add task
        </span>
      </span>
    ),
  },
];

/** Set it up step by step: the four screens, typed in and clicked through. */
export const StepsDemo: FC<{ playing: boolean }> = ({ playing }) => {
  const root = useRef<HTMLDivElement>(null);
  // Which frame's markup is mounted. Held in a ref-driven attribute rather
  // than state: the timeline swaps frames by toggling which one shows.
  useEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    const frames = [
      ...el.querySelectorAll<HTMLElement>('.c-demo-steps__frame'),
    ];
    const dots = [...el.querySelectorAll<HTMLElement>('.c-demo-steps__dots i')];
    const back = el.querySelector('.c-demo-steps__back') as HTMLElement;
    const next = el.querySelector('.c-demo-steps__next') as HTMLElement;

    const show = (i: number, filled: boolean) => {
      frames.forEach((f, j) => {
        f.hidden = j !== i;
      });
      dots.forEach((d, j) => {
        d.classList.toggle('is-on', j === i);
        d.classList.toggle('is-done', j < i);
      });
      const frame = frames[i];
      frame
        .querySelectorAll<HTMLElement>('.c-demo-steps__typed')
        .forEach((t) => {
          t.textContent = filled ? (t.dataset.full ?? '') : '';
          t.parentElement?.classList.remove('is-typing');
        });
      frame.querySelectorAll('.c-demo-steps__reveal').forEach((r) => {
        r.classList.toggle('is-in', filled);
        if (r.classList.contains('c-demo-steps__rule')) {
          r.classList.toggle('is-ok', filled);
        }
      });
      next.textContent = i === frames.length - 1 ? 'Create' : 'Next';
      next.classList.remove('is-press');
      back.classList.toggle('is-off', i === 0);
    };

    const rest = () => show(frames.length - 1, true);
    if (!playing || reducedMotion()) {
      rest();
      return undefined;
    }

    const t = timeline();
    (async () => {
      while (t.alive()) {
        for (let i = 0; i < frames.length && t.alive(); i += 1) {
          show(i, false);
          const frame = frames[i];
          frame.classList.remove('is-swap');
          // Restart the swap-in animation: a layout read between the two.
          frame.getBoundingClientRect();
          frame.classList.add('is-swap');
          if (!(await t.wait(450))) break;
          for (const typed of frame.querySelectorAll<HTMLElement>(
            '.c-demo-steps__typed'
          )) {
            typed.parentElement?.classList.add('is-typing');
            // eslint-disable-next-line no-await-in-loop
            if (!(await t.type(typed, typed.dataset.full ?? '', [55, 45])))
              break;
            typed.parentElement?.classList.remove('is-typing');
            // eslint-disable-next-line no-await-in-loop
            await t.wait(220);
          }
          if (!t.alive()) break;
          for (const r of frame.querySelectorAll('.c-demo-steps__reveal')) {
            r.classList.add('is-in');
            if (r.classList.contains('c-demo-steps__rule')) {
              window.setTimeout(() => r.classList.add('is-ok'), 160);
            }
            // eslint-disable-next-line no-await-in-loop
            if (!(await t.wait(320))) break;
          }
          if (!(await t.wait(420))) break;
          next.classList.add('is-press');
          // eslint-disable-next-line no-await-in-loop
          await t.wait(i === frames.length - 1 ? 2200 : 320);
        }
      }
    })();
    return () => {
      t.stop();
      rest();
    };
  }, [playing]);

  return (
    <div className="c-demo c-demo-steps" ref={root} aria-hidden="true">
      <div className="c-demo-steps__card">
        <div className="c-demo-steps__dots">
          {FRAMES.map((f) => (
            <i key={f.title} />
          ))}
        </div>
        {FRAMES.map((f) => (
          <div key={f.title} className="c-demo-steps__frame">
            <p className="c-demo-steps__title">
              {f.ka && (
                <img src={knowledgeLogo} alt="" width={16} height={16} />
              )}
              {f.title}
            </p>
            <div className="c-demo-steps__body">{f.body}</div>
          </div>
        ))}
        <div className="c-demo-steps__foot">
          <span className="c-demo-steps__back">Back</span>
          <span className="c-demo-steps__next">Next</span>
        </div>
      </div>
    </div>
  );
};
